interface FormattedDateTime {
  date: string;
  time: string;
  isValid: boolean;
}

export interface FormattedDateTimeWithTimezone extends FormattedDateTime {
  timeZoneLabel: string;
}

const dateFormatter = new Intl.DateTimeFormat('es-ES', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

const timeFormatter = new Intl.DateTimeFormat('es-ES', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false
});

/**
 * ISO strings without timezone (e.g. "2026-02-02T12:41:00") are interpreted by JavaScript
 * as local time, so the same string would show the same clock time in every timezone.
 * This normalizes such strings to UTC (append "Z") so the instant is correct and
 * display in the user's local timezone varies as expected.
 */
function normalizeToUtcIfNoOffset(dateInput: string | Date): string | Date {
  if (typeof dateInput !== 'string') return dateInput;
  if (dateInput.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateInput)) return dateInput;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateInput)) return dateInput + 'Z';
  return dateInput;
}

/**
 * Formats a date string or Date object into localized date and time strings.
 * The input is interpreted as UTC when it is an ISO string with "Z" or an offset,
 * or when it is an ISO string without offset (then treated as UTC).
 * Output is formatted in the user's local timezone (Intl.DateTimeFormat default when timeZone is not set).
 *
 * @param dateInput - Date string (e.g. ISO UTC '2025-10-28T16:00:00Z') or Date object
 * @param locale - Optional locale (defaults to 'es-ES')
 * @param normalizeToUtcWhenNoOffset - When true (default), ISO strings without timezone are treated as UTC. When false, they are interpreted as local time. Use false on the detail page so dates are not adjusted to user timezone.
 * @returns Object with formatted date, time, and validity flag
 */
export const formatDateTime = (
  dateInput: string | Date,
  locale: string = 'es-ES',
  normalizeToUtcWhenNoOffset: boolean = true
): FormattedDateTime => {
  try {
    const normalized = normalizeToUtcWhenNoOffset ? normalizeToUtcIfNoOffset(dateInput) : dateInput;
    const date = typeof normalized === 'string' ? new Date(normalized) : normalized;
    
    if (isNaN(date.getTime())) {
      return {
        date: '-',
        time: '-',
        isValid: false
      };
    }

    const customFormatter = createDateTimeFormatter(locale);
    return customFormatter(date);

  } catch (error) {
    console.error('Error formatting date:', error);
    return {
      date: '-',
      time: '-',
      isValid: false
    };
  }
};

/**
 * Returns the local timezone abbreviation or offset (e.g. "GMT-3") for a given date.
 * Uses Intl.DateTimeFormat with timeZoneName: 'short'. May return empty string in some environments.
 */
function getTimezoneLabel(date: Date, locale: string = 'es-ES'): string {
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart?.value ?? '';
  } catch {
    return '';
  }
}

/**
 * Formats a date with date, time and local timezone indicator.
 * Input is interpreted as UTC (e.g. ISO with "Z"); output is in the user's local timezone.
 * timeZoneLabel may be empty in some environments — the UI can show a fallback like "hora local".
 *
 * @param dateInput - Date string (e.g. ISO UTC) or Date object
 * @param locale - Optional locale (defaults to 'es-ES')
 * @param normalizeToUtcWhenNoOffset - When true (default), ISO strings without timezone are treated as UTC. When false, they are interpreted as local time.
 * @returns Object with date, time, timeZoneLabel, and isValid
 */
export const formatDateTimeWithTimezone = (
  dateInput: string | Date,
  locale: string = 'es-ES',
  normalizeToUtcWhenNoOffset: boolean = true
): FormattedDateTimeWithTimezone => {
  try {
    const normalized = normalizeToUtcWhenNoOffset ? normalizeToUtcIfNoOffset(dateInput) : dateInput;
    const date = typeof normalized === 'string' ? new Date(normalized) : normalized;

    if (isNaN(date.getTime())) {
      return {
        date: '-',
        time: '-',
        timeZoneLabel: '',
        isValid: false
      };
    }

    const base = formatDateTime(date, locale, normalizeToUtcWhenNoOffset);
    const timeZoneLabel = getTimezoneLabel(date, locale);

    return {
      ...base,
      timeZoneLabel
    };
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return {
      date: '-',
      time: '-',
      timeZoneLabel: '',
      isValid: false
    };
  }
};

export const formatDate = (dateInput: string | Date): string => {
  const { date } = formatDateTime(dateInput);
  return date;
};

/**
 * Formats only the time part
 */
export const formatTime = (dateInput: string | Date): string => {
  const { time } = formatDateTime(dateInput);
  return time;
};

export const createDateTimeFormatter = (locale: string) => {
  const customDateFormatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const customTimeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  return (dateInput: string | Date): FormattedDateTime => {
    try {
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      
      if (isNaN(date.getTime())) {
        return { date: '-', time: '-', isValid: false };
      }

      return {
        date: customDateFormatter.format(date),
        time: customTimeFormatter.format(date),
        isValid: true
      };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { date: '-', time: '-', isValid: false };
    }
  };
};

